import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input, Label } from "../ui/input";
import { Progress } from "../ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Building2, Check } from "lucide-react";
import { toast } from "sonner";

function Tri({ value, onChange, testid }) {
  const opts = [[true, "Yes"], [false, "No"], [null, "Not sure"]];
  return (
    <div className="flex gap-1" data-testid={testid}>
      {opts.map(([v, l]) => (
        <button key={l} type="button" onClick={() => onChange(v)}
          className={`px-2.5 py-1.5 rounded-sm border text-xs transition-colors ${value === v ? "border-accent bg-accent/10" : "border-border text-muted-foreground hover:bg-surface-hover"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

export default function FundingProfile() {
  const [p, setP] = useState(null);
  const [ref, setRef] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [r, rf] = await Promise.all([api.get("/susfin/profile"), api.get("/susfin/reference")]);
    setP(r.data); setF(r.data); setRef(rf.data);
  };
  useEffect(() => { load(); }, []);
  if (!p || !ref) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading profile…</div>;

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        uen: f.uen || null, incorporation_status: f.incorporation_status || null,
        ssic_code: f.ssic_code || null, sector: f.sector || null,
        company_type: f.company_type || null, listing_status: f.listing_status || null,
        group_revenue_sgd: num(f.group_revenue_sgd), group_employees: num(f.group_employees),
        local_shareholding_pct: num(f.local_shareholding_pct), years_in_operation: num(f.years_in_operation),
        operating_locations: typeof f.operating_locations === "string"
          ? f.operating_locations.split(",").map((s) => s.trim()).filter(Boolean)
          : (f.operating_locations || []),
        main_bank: f.main_bank || null,
        financially_viable: f.financially_viable ?? null, equipment_in_sg: f.equipment_in_sg ?? null,
        first_issb_report_intent: f.first_issb_report_intent ?? null,
      };
      const r = await api.put("/susfin/profile", body);
      setP(r.data); setF(r.data);
      toast.success("Funding profile saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const c = p.completeness || { pct: 0, missing: [] };
  const locations = Array.isArray(f.operating_locations) ? f.operating_locations.join(", ") : (f.operating_locations || "");

  return (
    <div className="space-y-5 max-w-3xl" data-testid="sf-profile">
      <div className="card-flat p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="label-eyebrow inline-flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-accent" /> Company Funding Profile</div>
          <div className="text-xs font-mono" data-testid="sf-profile-completeness">{c.pct}% complete</div>
        </div>
        <Progress value={c.pct} className="h-1.5 mb-2" />
        {c.missing.length > 0 && (
          <p className="text-[11px] text-muted-foreground" data-testid="sf-profile-missing">
            Missing: {c.missing.join(" · ")}
          </p>
        )}
        {c.pct === 100 && (
          <p className="text-[11px] text-[#00E65B] inline-flex items-center gap-1"><Check className="h-3 w-3" /> Profile complete — funding matches will use full eligibility data.</p>
        )}
        <p className="text-[11px] text-muted-foreground mt-1">
          This profile powers grant eligibility screening and financing matches across the module. Shared with the Grants engine.
        </p>
      </div>

      <div className="card-flat p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label>ACRA UEN</Label><Input value={f.uen || ""} onChange={(e) => set("uen", e.target.value)} data-testid="sf-profile-uen" /></div>
        <div><Label>SSIC code</Label><Input value={f.ssic_code || ""} onChange={(e) => set("ssic_code", e.target.value)} data-testid="sf-profile-ssic" /></div>
        <div><Label>Sector / industry</Label><Input value={f.sector || ""} onChange={(e) => set("sector", e.target.value)} data-testid="sf-profile-sector" /></div>
        <div><Label>Company type</Label>
          <Select value={f.company_type || "unset"} onValueChange={(v) => set("company_type", v === "unset" ? null : v)}>
            <SelectTrigger data-testid="sf-profile-company-type"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unset">—</SelectItem>
              {ref.company_types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select></div>
        <div><Label>Incorporation status</Label>
          <Select value={f.incorporation_status || "unset"} onValueChange={(v) => set("incorporation_status", v === "unset" ? null : v)}>
            <SelectTrigger data-testid="sf-profile-incorporation"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unset">—</SelectItem>
              {ref.incorporation_statuses.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select></div>
        <div><Label>Listing status</Label>
          <Select value={f.listing_status || "unset"} onValueChange={(v) => set("listing_status", v === "unset" ? null : v)}>
            <SelectTrigger data-testid="sf-profile-listing"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unset">—</SelectItem>
              {ref.listing_statuses.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select></div>
        <div>
          <Label>Group revenue (S$)</Label>
          <Input type="number" value={f.group_revenue_sgd ?? ""} onChange={(e) => set("group_revenue_sgd", e.target.value)} data-testid="sf-profile-revenue" />
          {p.revenue_band && <div className="text-[10px] text-muted-foreground mt-1">Band: {p.revenue_band}</div>}
        </div>
        <div>
          <Label>Group employees</Label>
          <Input type="number" value={f.group_employees ?? ""} onChange={(e) => set("group_employees", e.target.value)} data-testid="sf-profile-employees" />
          {p.employee_band && <div className="text-[10px] text-muted-foreground mt-1">Band: {p.employee_band}</div>}
        </div>
        <div><Label>Local shareholding (%)</Label><Input type="number" value={f.local_shareholding_pct ?? ""} onChange={(e) => set("local_shareholding_pct", e.target.value)} data-testid="sf-profile-shareholding" /></div>
        <div><Label>Years in operation</Label><Input type="number" value={f.years_in_operation ?? ""} onChange={(e) => set("years_in_operation", e.target.value)} data-testid="sf-profile-years" />
          {(f.years_in_operation === null || f.years_in_operation === undefined || f.years_in_operation === "") && <div className="text-[10px] text-muted-foreground mt-1" data-testid="sf-profile-years-gap">Not recorded — add if known</div>}
        </div>
        <div><Label>Operating locations (comma-separated)</Label><Input value={locations} onChange={(e) => set("operating_locations", e.target.value)} placeholder="Singapore HQ, Tuas plant" /></div>
        <div><Label>Main bank</Label><Input value={f.main_bank || ""} onChange={(e) => set("main_bank", e.target.value)} placeholder="DBS / UOB / OCBC…" /></div>
        <div><Label>Financially viable for projects</Label><Tri value={f.financially_viable ?? null} onChange={(v) => set("financially_viable", v)} testid="sf-profile-viable" /></div>
        <div><Label>Equipment will be used in SG</Label><Tri value={f.equipment_in_sg ?? null} onChange={(v) => set("equipment_in_sg", v)} testid="sf-profile-equipment" /></div>
        <div><Label>First ISSB-aligned report intent</Label><Tri value={f.first_issb_report_intent ?? null} onChange={(v) => set("first_issb_report_intent", v)} testid="sf-profile-issb" /></div>
        <div className="sm:col-span-2 flex justify-end">
          <Button variant="primary" onClick={save} disabled={busy} data-testid="sf-profile-save">{busy ? "Saving…" : "Save profile"}</Button>
        </div>
      </div>
    </div>
  );
}
